import {AbstractControl, UntypedFormArray, UntypedFormGroup} from '@angular/forms';
import {debounceTime} from 'rxjs/operators';
import {Feature, Geometry, Position} from '@turf/helpers';
import * as turf from '@turf/turf';
import {EventEmitter} from '@angular/core';
import {Ttm} from './classes/ttm';
import {TtmImage} from './classes/ttmimage';


// tslint:disable-next-line:ban-types
declare var ga: Function;

export class Utils {

    static compareValue(value: string) {
        return value.replace(/[^a-z0-9]/gi, '').toLowerCase();
    }

    static convertDataUrlToBlob(dataUrl): Blob {
        const bstr = atob(dataUrl);
        let n = bstr.length;
        const u8arr = new Uint8Array(n);

        while (n--) {
            u8arr[n] = bstr.charCodeAt(n);
        }

        return new Blob([u8arr], {type: 'image/jpg'});
    }

    static ttmEntityDistance(ttm: Ttm, image: TtmImage) {
        const useEntity = ttm.entities?.sort((a, b) => (!!b.line ? 2 : !!b.pole ? 1 : 0) - (!!a.line ? 2 : !!a.pole ? 1 : 0))
            .find(e => !!e.gnd);
        if (image.location && useEntity) {
            const distance = turf.distance(
                turf.point(useEntity.gnd.coordinates[0] as any),
                turf.point(image.location.coordinates as any), {units: 'meters'}
            );
            if (distance > 999) {
                return `${Math.round(distance / 1000)}km`;
            }
            return `${Math.round(distance)}m`;
        }
        return null;
    }

    static removeEmptyProps(obj: Object) {
        if (!obj) {
            return obj;
        }
        const returnObj = JSON.parse(JSON.stringify(obj));
        const propertyNames = Object.keys(returnObj);
        propertyNames.forEach(propertyName => {
            if (returnObj[propertyName] === null || returnObj[propertyName] === undefined
                || (typeof returnObj[propertyName] === 'object' && Object.keys(returnObj[propertyName]).length === 0)) {
                delete returnObj[propertyName];
            }
        });
        if (Object.keys(returnObj).length === 0) {
            return null;
        }
        return returnObj;
    }

    static getFileExtension(filename: string): string | null {
        const parts = filename.split('.');
        return parts.length > 1 ? parts.pop().toLowerCase() || null : null;
    }

    private static mapsCoreLibrary: Promise<google.maps.CoreLibrary>;

    // google.maps.LatLng/LatLngBounds only exist after the 'core' library has been imported by the async bootstrap loader
    static googleMapsCore(): Promise<google.maps.CoreLibrary> {
        if (!Utils.mapsCoreLibrary) {
            Utils.mapsCoreLibrary = google.maps.importLibrary('core') as Promise<google.maps.CoreLibrary>;
        }
        return Utils.mapsCoreLibrary;
    }

    static geoLatLng(value: Geometry): google.maps.LatLngLiteral {
        let useValue = value.coordinates;
        while (typeof useValue[0] !== 'number') {
            useValue = useValue[0];
        }
        return {lng: useValue[0], lat: +useValue[1]};
    }

    static latLngGeo(current: Geometry, value: google.maps.LatLng | google.maps.LatLngLiteral): Geometry {
        let newCenter = value instanceof google.maps.LatLng ? {lng: value.lng(), lat: value.lat()} : value as { lat: number; lng: number; };
        newCenter
        if (current.coordinates.length === 1) {
            return {type: 'MultiPoint', coordinates: [[newCenter.lng, newCenter.lat]]};
        }

        const point1 = turf.point(current.coordinates[0] as Position);
        const point2 = turf.point(current.coordinates[1] as Position);
        const newCenterT = turf.point([newCenter.lng, newCenter.lat]);

        const distance = turf.distance(point1, point2, {units: 'meters'});
        const bearing = turf.bearing(point1, point2);

        const newPoint2 = turf.destination(newCenterT, distance, bearing, {units: 'meters'});

        const newCoordinates2 = newPoint2.geometry.coordinates as [number, number];
        return {type: 'MultiPoint', coordinates: [[newCenter.lng, newCenter.lat], newCoordinates2]};
    }

    static convertGeometry(geo: Geometry, ...args: unknown[]): { lat: number, lng: number }[] {
        return geo.coordinates.map(c => {
            return {lat: c[1], lng: c[0]}
        });
    }

    static RestrictToNl() {
        return {
            latLngBounds: {
                north: 54,
                south: 50,
                west: 3.2,
                east: 7.4
            },
            strictBounds: false
        };
    }

    static isIOS() {
        if (typeof navigator === 'undefined') {
            return false;
        } else if (/iPad|iPhone|iPod/.test(navigator.platform)) {
            return true;
        } else {
            return navigator.maxTouchPoints &&
                navigator.maxTouchPoints > 2 &&
                /MacIntel/.test(navigator.platform);
        }
    }

    static getDateGetTime(date: Date, ignoreTime = false) {
        if (!date) {
            return date;
        }
        const newDate = (new Date(date));
        if (ignoreTime) {
            Utils.setTime(newDate);
        }
        return newDate.getTime();
    }

    static setTime(date: Date, hours = 0, minutes = 0) {
        if (!date) {
            return date;
        }
        if (typeof date === 'string') {
            date = new Date(date);
        }
        date.setHours(hours);
        date.setMinutes(minutes);
        date.setSeconds(0);
        date.setMilliseconds(0);
        return date;
    }

    static removeFunctions<T extends Record<string, any>>(obj: T): T | T[] {
        if (typeof obj !== 'object') {
            return obj;
        }
        if (Array.isArray(obj)) {
            return obj.map(object => {
                return Object.fromEntries(
                    Object.entries(object).filter(([_, value]) => {
                        return typeof value !== 'function' && !(value instanceof EventEmitter);
                    })
                ) as T;
            });
        }
        return Object.fromEntries(
            Object.entries(obj).filter(([_, value]) => {
                return typeof value !== 'function' && !(value instanceof EventEmitter);
            })
        ) as T;
    }

    static urlTitle = (d: string) => {
        return (d || '').replace(/\s+|\./g, '-')
            .replace(/[^a-zA-Z0-9\-]/gi, '')
            .replace(/\-+/g, '-')
            .toLowerCase().trim();
    };

    static triggerValidation(control: AbstractControl, source: AbstractControl[] = []) {
        if (control instanceof UntypedFormGroup) {
            const group = (control as UntypedFormGroup);

            for (const field in group.controls) {
                const c = group.controls[field];

                this.triggerValidation(c, source);
            }
        } else if (control instanceof UntypedFormArray) {
            const group = (control as UntypedFormArray);

            for (const field in group.controls) {
                const c = group.controls[field];

                this.triggerValidation(c, source);
            }
        } else {
            control.markAllAsTouched();
            control.updateValueAndValidity({emitEvent: source.indexOf(control) === -1});
        }

    }

    static triggerValidationP(control: AbstractControl, source: AbstractControl[] = []) {
        return new Promise((resolve, reject) => {
            const sub = control.statusChanges.pipe(debounceTime(5)).subscribe(status => {
                if (status === 'VALID') {
                    resolve(true);
                    sub.unsubscribe();
                }
                if (status === 'INVALID') {
                    reject();
                    sub.unsubscribe();
                }
            });

            Utils.triggerValidation(control, source);
        });
    }


    static getJwtData(token): {
        setupOtp: boolean,
        needsOtp: boolean,
        Token: string,
        TokenCreation: string,
        aud: string,
        exp: number,
        iss: string,
        name?: string
    } {
        if (!token) {
            return null;
        }
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(atob(base64).split('').map((c) => {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        }).join(''));

        return JSON.parse(jsonPayload);
    }

    static compare(a: number | string, b: number | string, isAsc: boolean) {
        a = ('' + a).trim().toLowerCase().replace(/[^a-zA-Z0-9]/, '');
        b = ('' + b).trim().toLowerCase().replace(/[^a-zA-Z0-9]/, '');
        if (!a.length) {
            a = isAsc ? 'zzzz' : 'aaaa';
        }
        if (!b.length) {
            b = isAsc ? 'zzzz' : 'aaaa';
        }
        return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
    }

    static sortTtms(items: Ttm[]): Ttm[] {
        if (!(items?.length > 0)) {
            return items;
        }
        // Zet de GeoItems om naar Turf-punten met een extra 'index'-property.
        const features = items.map((item, index) =>
            turf.point(item.entities[0]?.gnd.coordinates[0] as any, {index})
        );

        let groups: Record<string, Feature[]> = {};
        let distance = .5;
        do {
            const clustered = turf.clustersDbscan(turf.featureCollection(features), distance, {units: 'kilometers'});
            distance -= .05;

            groups = {};
            // Groepeer de features op cluster-id, of maak een unieke groep voor "ruis" (geen cluster).
            clustered.features.forEach(feature => {
                const key =
                    feature.properties?.cluster !== undefined
                        ? String(feature.properties.cluster)
                        : `noise-${(feature.properties as any)?.index}`;
                groups[key] = groups[key] || [];
                groups[key].push(feature);
            });
        }
        while (distance > 0 && Object.keys(groups).filter(k => !k.startsWith('noise-')).length < 4);
        // Sorteer elk cluster: eerst op aflopende latitude (noord eerst) en dan op oplopende longitude (west eerst).
        for (const group of Object.values(groups)) {
            group.sort((a, b) => {
                const [aLng, aLat] = (a.geometry as any).coordinates;
                const [bLng, bLat] = (b.geometry as any).coordinates;
                return bLat - aLat || aLng - bLng;
            });
        }

        // Sorteer de groepen zelf op basis van het eerste element in elk cluster.
        const sortedGroups = Object.values(groups).sort((a, b) => {
            const [aLng, aLat] = (a[0].geometry as any).coordinates;
            const [bLng, bLat] = (b[0].geometry as any).coordinates;
            return bLat - aLat || aLng - bLng;
        });

        // Combineer de gesorteerde groepen en zet de features terug om naar GeoItems.
        const sortedFeatures = sortedGroups.flat();
        return sortedFeatures.map(feature => items[(feature.properties! as any).index]);
    }

    static handleRelative(graphValues: { name: string; value: number; relative?: number; absolute?: number; }[]) {
        const total = graphValues.reduce((a, b) => a + b.value, 0);
        graphValues.forEach((oa) => {
            oa.absolute = oa.value;
            oa.relative = oa.value / total;
        });
    }


    static uniqifySvgIds(html: string) {
        const mapIds = {};

        // Find all IDs inside the SVG and replace them with unique IDs, add them to map to update references later
        html = html.replace(/id="([a-z0-9_\-]+)"/gi, function (zz, oldId) {
            const newId = oldId + Math.round(Math.random() * 99999999);
            mapIds[oldId] = newId;
            return `id="${newId}"`;
        });
        // Find all references to IDs in this SVG, when exists in the map, replace them
        html = html.replace(/#[a-z0-9_\-]+/gi, function (currentIdentifier) {
            if (mapIds.hasOwnProperty(currentIdentifier.substring(1))) {
                return `#${mapIds[currentIdentifier.substring(1)]}`;
            }
            return currentIdentifier;
        });

        return html;
    }


    static resize(callback: (file: File) => void, inpFile: File, size: number) {
        const img = new Image();
        img.src = URL.createObjectURL(inpFile);
        img.onload = () => {
            const factor = size / Math.max(img.naturalWidth, img.naturalHeight);
            const width = factor < 1 ? img.naturalWidth * factor : img.naturalWidth;
            const height = factor < 1 ? img.naturalHeight * factor : img.naturalHeight;
            const elem = document.createElement('canvas');
            elem.width = width;
            elem.height = height;
            const ctx = elem.getContext('2d');
            ctx.drawImage(img, 0, 0, width, height);
            let blobFile = null;
            ctx.canvas.toBlob((blobFileC) => {
                blobFile = blobFileC;
                callback(blobFileC as File);
            }, 'image/jpeg', .7);
        };
    }

}


export function debounce(delay: number = 100): MethodDecorator {
    return (target: any, propertyKey: string, descriptor: PropertyDescriptor) => {
        const timeoutKey = Symbol();

        const original = descriptor.value;

        descriptor.value = function (...args) {
            clearTimeout(this[timeoutKey]);
            this[timeoutKey] = setTimeout(() => original.apply(this, args), delay);
        };

        return descriptor;
    };
}
